import { c as _c } from "react/compiler-runtime";
import { createFieldErrorHandler, type VaultFormData } from '@onefootprint/core';
import type { FieldError } from '@onefootprint/hooks';
import type { Language, Locale, OnboardingRequirementCollectCustomData, VaultData } from '@onefootprint/request-types';
import { MarkdownText, PageShell } from '@onefootprint/ui';
import { type FormEvent, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ConditionalSection } from './components/conditional-section';
import { PromptRenderer } from './components/prompt-renderer';
import { formatFormData } from './utils/format-form-data';
import { getDefaultValues } from './utils/get-default-values';
export type CollectCustomDataFormData = VaultFormData;
type CollectCustomDataProps = {
  currentRequirement: OnboardingRequirementCollectCustomData;
  isLoading: boolean;
  decryptedVault: VaultData;
  /** `pressedIndex` is set when the page's authored buttons replace Continue. */
  onSubmit: (data: VaultData, onError: (errors: FieldError[]) => void, pressedIndex?: number) => void;
  className?: string;
  l10n?: {
    locale?: Locale;
    language?: Language;
  };
  disableSsnValidation?: boolean;
};
export const CollectCustomData = t0 => {
  const $ = _c(45);
  const {
    l10n,
    currentRequirement: t1,
    isLoading,
    decryptedVault,
    onSubmit,
    className,
    disableSsnValidation
  } = t0;
  const {
    page
  } = t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      keyPrefix: "collectCustomData.pages.customDataForm"
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useTranslation("idv2", t2);
  let t3;
  if ($[1] !== decryptedVault || $[2] !== page.sections) {
    t3 = getDefaultValues(decryptedVault, page.sections, decryptedVault);
    $[1] = decryptedVault;
    $[2] = page.sections;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t3) {
    t4 = {
      defaultValues: t3,
      mode: "onSubmit"
    };
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const methods = useForm(t4);
  const [pressedIndex, setPressedIndex] = useState();
  let buttons;
  let press;
  let t5;
  if ($[6] !== isLoading || $[7] !== methods || $[8] !== onSubmit || $[9] !== page.choice?.buttons || $[10] !== pressedIndex || $[11] !== t) {
    const handleFieldErrors = createFieldErrorHandler(methods);
    press = index => {
      setPressedIndex(index);
      methods.handleSubmit(data => onSubmit(formatFormData(data), handleFieldErrors, index))();
    };
    buttons = (page.choice?.buttons ?? []).map((button, index_0) => ({
      text: button.label.trim() || t(_temp),
      variant: button.style,
      type: "button" as const,
      onClick: () => press(index_0),
      loading: isLoading && pressedIndex === index_0,
      disabled: isLoading && pressedIndex !== index_0,
      actionName: `custom-data:button-${index_0}`
    }));
    t5 = buttons.findIndex(_temp2);
    $[6] = isLoading;
    $[7] = methods;
    $[8] = onSubmit;
    $[9] = page.choice?.buttons;
    $[10] = pressedIndex;
    $[11] = t;
    $[12] = buttons;
    $[13] = press;
    $[14] = t5;
  } else {
    buttons = $[12];
    press = $[13];
    t5 = $[14];
  }
  const primaryIndex = t5;
  let t6;
  if ($[15] !== isLoading || $[16] !== press || $[17] !== primaryIndex) {
    t6 = event => {
      event.preventDefault();
      if (isLoading) {
        return;
      }
      press(primaryIndex === -1 ? undefined : primaryIndex);
    };
    $[15] = isLoading;
    $[16] = press;
    $[17] = primaryIndex;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const handleSubmit = t6;
  const t7 = page.header;
  const t8 = page.subheader;
  let t9;
  if ($[19] !== buttons.length || $[20] !== t) {
    t9 = buttons.length > 0 ? undefined : {
      text: t(_temp3),
      type: "submit"
    };
    $[19] = buttons.length;
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = className || "flex flex-col gap-4 w-full";
  let t11;
  if ($[22] !== disableSsnValidation || $[23] !== l10n || $[24] !== page.sections) {
    let t12;
    if ($[26] !== disableSsnValidation || $[27] !== l10n) {
      t12 = section => {
        const key = section.kind === "prompt" ? section.di : section.content;
        return <ConditionalSection key={key} section={section}>{section.kind === "text_block" ? <MarkdownText className="text-body-2 text-primary">{section.content}</MarkdownText> : <PromptRenderer content={section} l10n={l10n} disableSsnValidation={disableSsnValidation} />}</ConditionalSection>;
      };
      $[26] = disableSsnValidation;
      $[27] = l10n;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    t11 = page.sections.map(t12);
    $[22] = disableSsnValidation;
    $[23] = l10n;
    $[24] = page.sections;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== buttons || $[33] !== isLoading || $[34] !== page.header || $[35] !== page.subheader || $[36] !== t12 || $[37] !== t9) {
    t13 = <PageShell title={t7} subtitle={t8} cta={t9} ctas={buttons} isLoading={isLoading}>{t12}</PageShell>;
    $[32] = buttons;
    $[33] = isLoading;
    $[34] = page.header;
    $[35] = page.subheader;
    $[36] = t12;
    $[37] = t9;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== handleSubmit || $[40] !== t13) {
    t14 = <form onSubmit={handleSubmit}>{t13}</form>;
    $[39] = handleSubmit;
    $[40] = t13;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== methods || $[43] !== t14) {
    t15 = <FormProvider {...methods}>{t14}</FormProvider>;
    $[42] = methods;
    $[43] = t14;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  return t15;
};
function _temp($) {
  return $.cta;
}
function _temp2(button_0) {
  return button_0.variant === "primary";
}
function _temp3($_0) {
  return $_0.cta;
}